import { useCallback, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Construction,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";

import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { toast } from "components/ui/use-toast";
import { cn } from "@/lib/utils";
import {
  useGetTabRowsQuery,
  useRefreshVarTabMutation,
  type VarRow,
  type VarTabData,
} from "@/services/tax-closing";
import {
  MATCH_FILTER_OPTIONS,
  hasMatchDetail,
  matchFilterLabel,
  matchFilterParams,
  matchReasonLabel,
  matchStatusKey,
  semMatchTotal,
  type MatchFilter,
  type MatchStatusKey,
} from "./var-match";
import { KpiCard } from "./kpi-card";
import { HintIcon } from "./hint-icon";

import { formatBRL } from "@/utils/money";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";
import { TAX_CLOSING_TOASTS } from "../messages";

interface VarTabProps {
  data: VarTabData & { rowCount?: number };
  jobId: string;
}

type VarCellHighlight = "yellow" | "sky" | null;

export const COLUMN_HINT_ARIA_LABEL = "O que esta coluna representa";

const COLUMN_HINT_DESCONTO =
  "Desconto concedido na venda, registrado no documento do PDV. O Valor Contábil é bruto e o PROCV vem do GFT já líquido: Valor Contábil − Desconto é o valor que deve bater com o GFT. Destacado em azul quando há desconto na nota.";

const COLUMN_HINT_PROCV =
  "Valor da mesma nota no GFT, já líquido de desconto. Aparece em vermelho quando a nota não foi localizada lá, e em branco quando não há como comparar.";

const COLUMN_HINT_DIF =
  "Diferença entre o GFT e o valor líquido da VAR (Valor Contábil − Desconto). Zero significa que as duas bases conferem; qualquer valor diferente de zero indica divergência, tanto para mais quanto para menos.";

interface ColumnConfig {
  key: keyof VarRow;
  label: string;
  type:
    | "number"
    | "text"
    | "date"
    | "mono"
    | "money"
    | "money-desconto"
    | "money-gft"
    | "dif"
    | "status"
    | "contingencia"
    | "venda-fechada"
    | "match";
  bg?: VarCellHighlight;
  hint?: string;
}

const VAR_BG_CLASS: Record<NonNullable<VarCellHighlight>, string> = {
  yellow: "bg-yellow-50",
  sky: "bg-sky-50",
};

const VAR_BG_HEADER_CLASS: Record<NonNullable<VarCellHighlight>, string> = {
  yellow: "bg-yellow-100/60",
  sky: "bg-sky-100/60",
};

const COLUMNS: ColumnConfig[] = [
  { key: "loja", label: "Loja", type: "number" },
  { key: "serie", label: "Serie", type: "number" },
  { key: "nfce", label: "NFC-e", type: "number" },
  { key: "contingencia", label: "NFC-e em Contingencia", type: "contingencia" },
  { key: "data", label: "Data", type: "date" },
  { key: "hora", label: "Hora", type: "text" },
  { key: "status", label: "Status", type: "status" },
  { key: "valorContabil", label: "Valor Contabil", type: "money" },
  {
    key: "valorDesconto",
    label: "Desconto",
    type: "money-desconto",
    bg: "sky",
    hint: COLUMN_HINT_DESCONTO,
  },
  { key: "chave", label: "Chave", type: "mono" },
  { key: "protocolo", label: "Protocolo", type: "mono" },
  { key: "vendaFechada", label: "Venda Fechada", type: "venda-fechada" },
  { key: "docCod", label: "DocCod", type: "number" },
  {
    key: "valorContabilGft",
    label: "PROCV",
    type: "money-gft",
    bg: "yellow",
    hint: COLUMN_HINT_PROCV,
  },
  { key: "dif", label: "DIF", type: "dif", bg: "yellow", hint: COLUMN_HINT_DIF },
  { key: "matched", label: "Match GFT", type: "match" },
];

const MATCH_STATUS_DISPLAY: Record<
  MatchStatusKey,
  { label: string; className: string } | null
> = {
  com: { label: "Com match", className: "bg-green-100 text-green-800 border-green-200" },
  sem: { label: "Sem match", className: "bg-red-100 text-red-800 border-red-200" },
  notComparable: {
    label: "Não comparável",
    className: "bg-slate-100 text-slate-600 border-slate-200",
  },
  unknown: null,
};

const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 20;

const STATUS_BADGE_CLASSES: Record<VarRow["status"], string> = {
  Producao: "bg-green-100 text-green-800 border-green-200",
  Cancelada: "bg-red-100 text-red-800 border-red-200",
};

export function VarTab({ data, jobId }: VarTabProps) {
  const rowCount = data.rowCount ?? data.rows?.length ?? 0;
  const isEmpty = !data.available || rowCount === 0;

  const [refreshVarTab, { isLoading: isRefreshing }] =
    useRefreshVarTabMutation();

  const handleRefresh = useCallback(async () => {
    try {
      const result = await refreshVarTab(jobId).unwrap();
      const summary = result.data.var.summary;
      toast(
        TAX_CLOSING_TOASTS.varRefreshSuccess(
          summary.totalRows,
          semMatchTotal(summary),
          summary.rowsWithDivergence,
        ),
      );
    } catch (err) {
      const message =
        (err as { data?: { message?: string }; status?: number })?.data
          ?.message ?? "Erro ao atualizar a aba VAR";
      toast(TAX_CLOSING_TOASTS.varRefreshError(message));
    }
  }, [jobId, refreshVarTab]);

  if (isEmpty) {
    return (
      <EmptyState
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />
    );
  }
  return (
    <VarDataTable
      data={data}
      jobId={jobId}
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
    />
  );
}

interface EmptyStateProps {
  onRefresh: () => void;
  isRefreshing: boolean;
}

function EmptyState({ onRefresh, isRefreshing }: EmptyStateProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        minHeight: 200,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          textAlign: "center",
        }}
      >
        {isRefreshing ? (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm font-medium">
              Cruzando NFC-e do PDV com o livro fiscal...
            </p>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 w-full">
            <Construction className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">
              Dados VAR ainda não calculados para esta extração
            </p>
            <p className="text-xs text-muted-foreground">
              Cruza NFC-e do PDV (DOC001) com o GFT por chave de acesso
            </p>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 mt-1 w-full"
              onClick={onRefresh}
            >
              <RefreshCw className="h-4 w-4" />
              Calcular agora
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

interface VarDataTableProps {
  data: VarTabData;
  jobId: string;
  onRefresh: () => void;
  isRefreshing: boolean;
}

function VarDataTable({ data, jobId, onRefresh, isRefreshing }: VarDataTableProps) {
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [sortKey, setSortKey] = useState<string>("dif");
  const [sortAsc, setSortAsc] = useState(false);
  const [matchFilter, setMatchFilter] = useState<MatchFilter>("todos");
  const [selectedRow, setSelectedRow] = useState<VarRow | null>(null);

  const searchTimerRef = useMemo(() => ({ id: null as ReturnType<typeof setTimeout> | null }), []);
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimerRef.id) clearTimeout(searchTimerRef.id);
    searchTimerRef.id = setTimeout(() => {
      setSearchDebounced(value);
      setPage(1);
    }, 400);
  };

  const { data: rowsData, isFetching } = useGetTabRowsQuery({
    jobId,
    tab: "var",
    page,
    limit: pageSize,
    sort: sortKey || undefined,
    order: sortAsc ? "asc" : "desc",
    search: searchDebounced || undefined,
    ...matchFilterParams(matchFilter),
  });

  const paged = (rowsData?.rows ?? []) as unknown as VarRow[];
  const meta = rowsData?.meta;
  const totalPages = meta?.totalPages ?? 1;

  const toggleSort = (key: keyof VarRow) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(false);
    }
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-4">
      <SummaryStrip data={data} />

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar em todas as colunas..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>
          <select
            value={matchFilter}
            onChange={(e) => {
              setMatchFilter(e.target.value as MatchFilter);
              setPage(1);
            }}
            className="h-9 shrink-0 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            title="Filtrar por match no GFT"
          >
            {MATCH_FILTER_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {matchFilterLabel(opt)}
              </option>
            ))}
          </select>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 shrink-0"
          onClick={onRefresh}
          disabled={isRefreshing}
          title="Recalcula a aba VAR sem reextrair o GFT"
        >
          {isRefreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Recalcular VAR
        </Button>
      </div>

      <div className="relative overflow-x-auto border rounded-lg">
        {isFetching && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="border-b bg-muted/40">
              {COLUMNS.map((col) => {
                const isSorted = sortKey === col.key;
                const SortIcon = isSorted ? (sortAsc ? ArrowUp : ArrowDown) : ArrowUpDown;
                return (
                  <th
                    key={col.key}
                    className={cn(
                      "py-2.5 px-3 text-left font-medium text-xs uppercase tracking-wider cursor-pointer hover:bg-muted/60 select-none transition-colors",
                      col.bg && VAR_BG_HEADER_CLASS[col.bg],
                      isSorted ? "text-foreground" : "text-muted-foreground",
                    )}
                    onClick={() => toggleSort(col.key)}
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      <SortIcon className={cn("h-3 w-3", isSorted ? "opacity-100" : "opacity-30")} />
                      {col.hint ? (
                        <HintIcon
                          text={col.hint}
                          ariaLabel={COLUMN_HINT_ARIA_LABEL}
                        />
                      ) : null}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paged.map((row, idx) => (
              <VarRowEl key={`${row.loja}-${row.docCod}-${idx}`} row={row} striped={idx % 2 === 1} columns={COLUMNS} onSelect={setSelectedRow} />
            ))}
            {paged.length === 0 && !isFetching && (
              <tr>
                <td colSpan={COLUMNS.length} className="py-12 text-center text-muted-foreground">
                  Nenhum registro encontrado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-3 text-muted-foreground">
          <span>
            {isFetching ? "Carregando..." : (
              <>
                {meta?.totalFiltered?.toLocaleString("pt-BR") ?? 0} de {meta?.total?.toLocaleString("pt-BR") ?? 0} registros &middot; Página {page} de {Math.max(1, totalPages)}
              </>
            )}
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-xs">Itens:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage(page - 1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || isFetching}
            onClick={() => setPage(page + 1)}
          >
            Próximo <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      <VarMatchDetailDialog
        row={selectedRow}
        onClose={() => setSelectedRow(null)}
      />
    </div>
  );
}

function VarRowEl({ row, striped, columns, indented, onSelect }: { row: VarRow; striped: boolean; columns: ColumnConfig[]; indented?: boolean; onSelect?: (row: VarRow) => void }) {
  const notComparable = row.notComparableReason !== null;
  const noMatch = !notComparable && !row.matched;
  const dif = parseFloat(row.dif.replace(",", "."));
  const hasDivergence = !notComparable && !isNaN(dif) && dif !== 0;
  const clickable = hasMatchDetail(row);

  return (
    <tr
      className={cn(
        "border-b transition-colors hover:bg-muted/20",
        striped ? "bg-muted/10" : "bg-background",
        noMatch && "bg-red-50 hover:bg-red-100",
        !noMatch && hasDivergence && "bg-amber-50/50 hover:bg-amber-100/50",
        notComparable && "bg-slate-100 text-muted-foreground hover:bg-slate-200",
        indented && "bg-muted/5 hover:bg-muted/15",
        clickable && "cursor-pointer",
      )}
      onClick={clickable ? () => onSelect?.(row) : undefined}
    >
      {columns.map((col, cIdx) => (
        <td
          key={col.key}
          className={cn(
            "py-2.5 px-3",
            col.bg && VAR_BG_CLASS[col.bg],
            indented && cIdx === 0 && "pl-10",
          )}
        >
          {renderCell(col, row)}
        </td>
      ))}
    </tr>
  );
}

function VarMatchDetailDialog({ row, onClose }: { row: VarRow | null; onClose: () => void }) {
  const reason = row ? matchReasonLabel(row) : null;
  const display = row ? MATCH_STATUS_DISPLAY[matchStatusKey(row)] : null;
  return (
    <Dialog open={row !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Detalhe da nota</DialogTitle>
        </DialogHeader>
        {row && (
          <div className="flex flex-col gap-4 text-sm mt-2">
            <div className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                Motivo
              </span>
              <Badge
                variant="outline"
                className={cn("w-fit font-medium", display?.className)}
              >
                {reason ?? "—"}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <DetailField label="Loja" value={String(row.loja)} />
              <DetailField label="Série" value={String(row.serie)} />
              <DetailField label="NFC-e" value={String(row.nfce)} />
              <DetailField label="Cupom" value={String(row.cupom)} />
              <DetailField label="DocCod" value={String(row.docCod)} />
              <DetailField label="Protocolo" value={row.protocolo ?? "—"} />
              <DetailField label="Data" value={formatDateToDMY(row.data)} />
              <DetailField label="Hora" value={row.hora} />
              <DetailField label="Valor Contábil" value={formatBRL(row.valorContabil)} />
              <DetailField label="Desconto" value={formatBRL(row.valorDesconto)} />
              <DetailField label="Status" value={row.status} />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                Chave de acesso
              </span>
              <span className="font-mono text-xs break-all">{row.chave || "—"}</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

type CellRenderer = (col: ColumnConfig, row: VarRow) => JSX.Element;

const CELL_RENDERERS: Record<ColumnConfig["type"], CellRenderer> = {
  number: (col, row) => <span className="tabular-nums">{String(row[col.key] ?? "")}</span>,
  text: (col, row) => <span>{String(row[col.key] ?? "")}</span>,
  date: (col, row) => {
    const val = row[col.key];
    if (val === null || val === undefined || val === "") {
      return <span className="text-muted-foreground">—</span>;
    }
    return <span className="tabular-nums">{formatDateToDMY(String(val))}</span>;
  },
  mono: (col, row) => {
    const val = row[col.key];
    if (val === null || val === undefined || val === "") {
      return <span className="text-muted-foreground">—</span>;
    }
    return <span className="font-mono text-xs">{String(val)}</span>;
  },
  money: (col, row) => (
    <span className="tabular-nums">{formatBRL(String(row[col.key] ?? "0"))}</span>
  ),
  "money-desconto": (_col, row) => {
    const num = parseFloat(String(row.valorDesconto ?? "0").replace(",", "."));
    const formatted = formatBRL(String(row.valorDesconto ?? "0"));
    if (isNaN(num) || num === 0) {
      return <span className="tabular-nums text-muted-foreground">{formatted}</span>;
    }
    return (
      <span className="tabular-nums font-semibold text-sky-700">{formatted}</span>
    );
  },
  "money-gft": (_col, row) => {
    if (row.notComparableReason !== null) {
      return <span className="text-muted-foreground">—</span>;
    }
    return (
      <span
        className={cn(
          "tabular-nums",
          !row.matched ? "text-red-600 font-semibold" : "text-blue-700 font-medium",
        )}
      >
        {formatBRL(row.valorContabilGft)}
      </span>
    );
  },
  dif: (_col, row) => {
    if (row.notComparableReason !== null) {
      return <span className="text-muted-foreground">—</span>;
    }
    const num = parseFloat(row.dif.replace(",", "."));
    return (
      <span
        className={cn(
          "tabular-nums font-semibold",
          !isNaN(num) && num !== 0 && "text-red-600",
          !isNaN(num) && num === 0 && "text-muted-foreground",
        )}
      >
        {formatBRL(row.dif)}
      </span>
    );
  },
  status: (_col, row) => (
    <Badge variant="outline" className={cn("font-medium", STATUS_BADGE_CLASSES[row.status])}>
      {row.status}
    </Badge>
  ),
  contingencia: (_col, row) => {
    if (!row.contingencia || row.contingencia === 0) {
      return <span className="tabular-nums text-muted-foreground">0</span>;
    }
    return (
      <span className="tabular-nums font-semibold text-amber-700">
        {row.contingencia}
      </span>
    );
  },
  "venda-fechada": (_col, row) => (
    <Badge
      variant="outline"
      className={cn(
        "font-medium",
        row.vendaFechada === "Sim"
          ? "bg-green-100 text-green-800 border-green-200"
          : "bg-red-100 text-red-800 border-red-200",
      )}
    >
      {row.vendaFechada}
    </Badge>
  ),
  match: (_col, row) => {
    const display = MATCH_STATUS_DISPLAY[matchStatusKey(row)];
    if (!display) {
      return <span className="text-muted-foreground">—</span>;
    }
    return (
      <Badge variant="outline" className={cn("font-medium", display.className)}>
        {display.label}
      </Badge>
    );
  },
};

function renderCell(col: ColumnConfig, row: VarRow): JSX.Element {
  return CELL_RENDERERS[col.type](col, row);
}

const VAR_KPI_HINTS = {
  totalRows:
    "Total de NFC-e da base VAR nas lojas e no período do fechamento.",
  contingency:
    "NFC-e emitidas em contingência, quando a autorização online não estava disponível no momento da venda.",
  withoutMatch:
    "NFC-e da VAR não localizadas no GFT pela composição loja + modelo + série + cupom. Inclui as notas sem cupom, que não são comparáveis.",
  notComparable:
    "Parcela do indicador anterior sem número de cupom: não há como comparar com o GFT, e essas notas ficam fora do cálculo de divergência e do DIF.",
  withDivergence:
    "NFC-e comparáveis cujo valor líquido da VAR (Valor Contábil - Desconto) difere do valor da nota no GFT, que já vem líquido.",
  totalDif:
    "Soma das diferenças (GFT - VAR líquido) de todas as notas comparáveis. Quanto mais longe de zero, maior a divergência — para cima ou para baixo.",
} as const;

function SummaryStrip({ data }: { data: VarTabData }) {
  const { summary } = data;
  const semMatch = semMatchTotal(summary);
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
      <KpiCard
        label="Notas VAR"
        value={summary.totalRows.toLocaleString("pt-BR")}
        hint={VAR_KPI_HINTS.totalRows}
      />
      <KpiCard
        label="Em Contingência"
        value={summary.rowsInContingency.toLocaleString("pt-BR")}
        tone={summary.rowsInContingency > 0 ? "amber" : "neutral"}
        hint={VAR_KPI_HINTS.contingency}
      />
      <KpiCard
        label="Sem Match no GFT"
        value={semMatch.toLocaleString("pt-BR")}
        tone={semMatch > 0 ? "red" : "neutral"}
        icon={semMatch > 0 ? <AlertTriangle className="h-4 w-4" /> : undefined}
        hint={VAR_KPI_HINTS.withoutMatch}
      />
      <KpiCard
        label="↳ dos quais sem cupom"
        value={summary.rowsNotComparable.toLocaleString("pt-BR")}
        tone={summary.rowsNotComparable > 0 ? "amber" : "neutral"}
        hint={VAR_KPI_HINTS.notComparable}
      />
      <KpiCard
        label="Com Divergência"
        value={summary.rowsWithDivergence.toLocaleString("pt-BR")}
        tone={summary.rowsWithDivergence > 0 ? "amber" : "neutral"}
        hint={VAR_KPI_HINTS.withDivergence}
      />
      <KpiCard
        label="DIF Total"
        value={formatBRL(summary.totalDif)}
        hint={VAR_KPI_HINTS.totalDif}
      />
    </div>
  );
}
